// API Request Logger and Rate Limiter with Deduplication
class APILogger {
  private requestLog: Array<{
    timestamp: number;
    endpoint: string;
    method: string;
    userId?: string;
    signature: string;
  }> = [];

  private rateLimitMap = new Map<string, number[]>();
  private duplicateRequestMap = new Map<
    string,
    { count: number; lastSeen: number }
  >();
  private readonly DUPLICATE_WINDOW = 5000; // 5 seconds

  log(endpoint: string, method: string = 'GET', userId?: string) {
    const timestamp = Date.now();
    const signature = `${method}:${endpoint}:${userId || 'unknown'}`;

    // Check for duplicate requests within the time window
    const existing = this.duplicateRequestMap.get(signature);
    if (existing && timestamp - existing.lastSeen < this.DUPLICATE_WINDOW) {
      existing.count++;
      existing.lastSeen = timestamp;
      console.warn(
        `[API Request] DUPLICATE (${
          existing.count
        }x) ${method} ${endpoint} (User: ${userId || 'unknown'})`
      );
      return; // Don't log duplicates to main request log
    } else {
      // Reset or create new tracking
      this.duplicateRequestMap.set(signature, {
        count: 1,
        lastSeen: timestamp,
      });
    }

    this.requestLog.push({
      timestamp,
      endpoint,
      method,
      userId,
      signature,
    });

    // Keep only last 1000 requests to prevent memory issues
    if (this.requestLog.length > 1000) {
      this.requestLog = this.requestLog.slice(-1000);
    }

    // Log to console for monitoring
    // console.log(`[API Request] ${method} ${endpoint} (User: ${userId || 'unknown'}) at ${new Date(timestamp).toISOString()}`);
  }

  getRequestsInLastHour(): number {
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    return this.requestLog.filter((req) => req.timestamp > oneHourAgo).length;
  }

  getRequestsByEndpoint(endpoint: string, timeWindow = 60 * 60 * 1000): number {
    const cutoff = Date.now() - timeWindow;
    return this.requestLog.filter(
      (req) => req.timestamp > cutoff && req.endpoint.includes(endpoint)
    ).length;
  }

  getRecentRequests(minutes = 10) {
    const cutoff = Date.now() - minutes * 60 * 1000;
    return this.requestLog.filter((req) => req.timestamp > cutoff);
  }

  checkRateLimit(key: string, maxRequests = 10, windowMs = 60000): boolean {
    const now = Date.now();
    const windowStart = now - windowMs;

    if (!this.rateLimitMap.has(key)) {
      this.rateLimitMap.set(key, []);
    }

    const requests = this.rateLimitMap.get(key)!;

    // Remove old requests outside the window
    const validRequests = requests.filter(
      (timestamp) => timestamp > windowStart
    );

    if (validRequests.length >= maxRequests) {
      console.warn(
        `[Rate Limit] Key "${key}" exceeded ${maxRequests} requests in ${windowMs}ms`
      );
      return false;
    }

    // Add current request
    validRequests.push(now);
    this.rateLimitMap.set(key, validRequests);

    return true;
  }

  getStats() {
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    const recentRequests = this.requestLog.filter(
      (req) => req.timestamp > oneHourAgo
    );

    const byEndpoint = recentRequests.reduce((acc, req) => {
      acc[req.endpoint] = (acc[req.endpoint] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const byUser = recentRequests.reduce((acc, req) => {
      const userId = req.userId || 'unknown';
      acc[userId] = (acc[userId] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Get duplicate statistics
    const duplicateStats = Array.from(this.duplicateRequestMap.entries())
      .filter(([_, data]) => data.count > 1)
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 10);

    return {
      totalLastHour: recentRequests.length,
      byEndpoint,
      byUser,
      duplicates: duplicateStats.map(([signature, data]) => ({
        signature,
        count: data.count,
        lastSeen: new Date(data.lastSeen).toISOString(),
      })),
      timeline: this.getHourlyTimeline(),
    };
  }

  private getHourlyTimeline() {
    const now = Date.now();
    const timeline = [];

    for (let i = 11; i >= 0; i--) {
      const hourStart = now - i * 60 * 60 * 1000;
      const hourEnd = hourStart + 60 * 60 * 1000;
      const count = this.requestLog.filter(
        (req) => req.timestamp >= hourStart && req.timestamp < hourEnd
      ).length;

      timeline.push({
        hour: new Date(hourStart).toISOString().substr(11, 2) + ':00',
        count,
      });
    }

    return timeline;
  }

  // Alert if API usage is excessive
  monitorUsage() {
    const stats = this.getStats();

    if (stats.totalLastHour > 100) {
      console.warn(
        `[API Monitor] High API usage detected: ${stats.totalLastHour} requests in the last hour`
      );
      console.table(stats.byEndpoint);
    }

    return stats;
  }
}

export const apiLogger = new APILogger();

// REMOVED: Monitor setInterval that was causing background polling
// Now monitoring is only done when explicitly requested
